import type { ChangeEvent } from 'react'
import { cn } from '@/common/lib/cn/cn'
import { Icon } from '@/common/ui/Icon/Icon'
import type { IconName } from '@/common/ui/Icon/Icon'
import { Select, SelectOption } from '@/common/ui/Form/Select'

export type ListSortFieldOption<T extends string = string> = {
    key: T
    label: string
}

const getListSortIconName = (direction: 'asc' | 'desc'): IconName => (direction === 'asc' ? 'sort-asc' : 'sort')

export type ListSortProps<T extends string = string> = {
    options: ListSortFieldOption<T>[]
    value: string
    direction: 'asc' | 'desc'
    onChange: (e: ChangeEvent<HTMLSelectElement>) => void
    highlightActive?: boolean
    'aria-label': string
    sortIconName?: (direction: 'asc' | 'desc') => IconName
    className?: string
}

/** Компактный Select сортировки для тулбара (ghost, только иконка на кнопке). */
export const ListSort = <T extends string = string>({
    options,
    value,
    direction,
    onChange,
    highlightActive,
    'aria-label': ariaLabel,
    sortIconName = getListSortIconName,
    className,
}: ListSortProps<T>) => (
    <Select
        variant='ghost'
        icon={<Icon name={sortIconName(direction)} size='lg' />}
        value={value}
        onChange={onChange}
        size='lg'
        emphasis='accent-secondary'
        aria-label={ariaLabel}
        className={cn('shrink-0', { 'btn-active': highlightActive }, className)}
        showText={false}
        showChevron={false}
    >
        {options.map(option => {
            const isActive = value === option.key
            const rowInList = (
                <span className='group flex w-full min-w-0 items-center justify-between gap-3'>
                    <span className='min-w-0 truncate'>{option.label}</span>
                    <span
                        className={cn(
                            'inline-flex shrink-0 transition-opacity',
                            isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                        )}
                    >
                        {isActive ? <Icon name={sortIconName(direction)} size='lg' /> : <Icon name='sort' size='lg' />}
                    </span>
                </span>
            )
            return (
                <SelectOption key={option.key} value={option.key} renderContent={rowInList}>
                    {option.label}
                </SelectOption>
            )
        })}
    </Select>
)
